import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, Medal, Crown, Flame, Users, TrendingUp, 
  Award, ChevronRight, Dumbbell, Apple, Moon, Eye
} from 'lucide-react';
import axios from 'axios';

const SupporterLeaderboardPage = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalAthletes: 0, avgScore: 0, topScore: 0 });

  useEffect(() => {
    fetchLinkedAthletes();
  }, []);

  const fetchLinkedAthletes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/supporter/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sortedAthletes = (response.data.athletes || []).sort(
        (a, b) => (b.aura_score || 0) - (a.aura_score || 0)
      );
      
      setAthletes(sortedAthletes);
      
      if (sortedAthletes.length > 0) {
        const scores = sortedAthletes.map(a => a.aura_score || 0);
        setStats({
          totalAthletes: sortedAthletes.length,
          avgScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
          topScore: Math.max(...scores)
        });
      }
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-aura-text-muted">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-yellow-500/40';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-300/10 border-gray-400/40';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/40';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  const getStreakBadge = (streak) => {
    if (streak >= 30) return { label: '30+ Day Streak', color: 'bg-orange-500/20 text-orange-400' };
    if (streak >= 14) return { label: '14+ Day Streak', color: 'bg-yellow-500/20 text-yellow-400' };
    if (streak >= 7) return { label: '7+ Day Streak', color: 'bg-amber-500/20 text-amber-400' };
    if (streak >= 3) return { label: `${streak} Day Streak`, color: 'bg-green-500/20 text-green-400' };
    return null;
  };

  const getCompletionColor = (percent) => {
    if (percent >= 80) return 'text-green-400';
    if (percent >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
            My Athletes Leaderboard
          </h1>
          <p className="text-aura-text-muted mt-2">
            Compare your linked athletes' progress and performance
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{stats.totalAthletes}</p>
              <p className="text-xs text-aura-text-muted uppercase">Athletes</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-aura-secondary mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{stats.avgScore}</p>
              <p className="text-xs text-aura-text-muted uppercase">Avg Score</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Award className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{stats.topScore}</p>
              <p className="text-xs text-aura-text-muted uppercase">Top Score</p>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-aura-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-aura-text-muted">Loading athletes...</p>
              </div>
            ) : athletes.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-white font-medium">No linked athletes yet</p>
                <p className="text-aura-text-muted text-sm mt-1">
                  Link athletes from your dashboard to see their rankings
                </p>
                <Link to="/supporter">
                  <Button className="mt-4 bg-aura-primary hover:bg-aura-primary-hover">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            ) : (
              athletes.map((athlete, index) => {
                const rank = index + 1;
                const streakBadge = getStreakBadge(athlete.current_streak || 0);
                const weeklyWorkoutPercent = Math.min(100, ((athlete.workouts_this_week || 0) / 5) * 100);
                const nutritionPercent = Math.min(100, ((athlete.meals_today || 0) / 3) * 100);
                
                return (
                  <div 
                    key={athlete.user_id}
                    className={`p-4 rounded-xl border ${getRankBg(rank)} transition-all hover:scale-[1.01]`}
                    data-testid={`athlete-rank-${rank}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankIcon(rank)}
                      </div>
                      
                      {/* Avatar & Name */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-aura-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {athlete.picture ? (
                            <img src={athlete.picture} alt={athlete.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-aura-primary">
                              {athlete.name?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-heading font-bold text-white truncate">
                            {athlete.name}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {athlete.sport && (
                              <span className="text-xs text-aura-text-muted">{athlete.sport}</span>
                            )}
                            {streakBadge && (
                              <Badge className={`${streakBadge.color} border-0 text-xs`}>
                                <Flame className="w-3 h-3 mr-1" />
                                {streakBadge.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Weekly Stats */}
                      <div className="hidden md:flex items-center gap-4">
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <Dumbbell className="w-4 h-4 text-aura-primary" />
                            <span className={`text-sm font-bold ${getCompletionColor(weeklyWorkoutPercent)}`}>
                              {athlete.workouts_this_week || 0}/5
                            </span>
                          </div>
                          <p className="text-xs text-aura-text-muted">Workouts</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <Apple className="w-4 h-4 text-green-400" />
                            <span className={`text-sm font-bold ${getCompletionColor(nutritionPercent)}`}>
                              {athlete.meals_today || 0}/3
                            </span>
                          </div>
                          <p className="text-xs text-aura-text-muted">Meals</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <Moon className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-bold text-white">
                              {athlete.sleep_hours || '--'}h
                            </span>
                          </div>
                          <p className="text-xs text-aura-text-muted">Sleep</p>
                        </div>
                      </div>
                      
                      {/* Aura Score */}
                      <div className="text-right">
                        <p className={`font-heading font-bold text-2xl ${rank <= 3 ? 'text-yellow-400' : 'text-white'}`}>
                          {(athlete.aura_score || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-aura-text-muted">Aura</p>
                      </div>
                      
                      {/* View Button */}
                      <Link to={`/supporter/athlete/${athlete.user_id}`}>
                        <Button variant="ghost" size="sm" className="text-aura-text-muted hover:text-white">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Mobile Stats (shown on small screens) */}
                    <div className="flex md:hidden items-center justify-between mt-3 pt-3 border-t border-white/10">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Dumbbell className="w-3 h-3 text-aura-primary" />
                          <span className="text-xs text-white">{athlete.workouts_this_week || 0}/5</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Apple className="w-3 h-3 text-green-400" />
                          <span className="text-xs text-white">{athlete.meals_today || 0}/3</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Moon className="w-3 h-3 text-purple-400" />
                          <span className="text-xs text-white">{athlete.sleep_hours || '--'}h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Tips Card */}
        {athletes.length > 0 && (
          <Card className="bg-gradient-to-r from-purple-500/10 to-purple-600/5 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Help Your Athletes Climb</p>
                  <p className="text-sm text-aura-text-muted mt-1">
                    Encourage consistent workouts, meal logging, and recovery tracking. 
                    Athletes with streaks earn bonus points!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SupporterLeaderboardPage;
